import { lazy } from 'react';

import {
  AssetConfigureTimeSeriesPage,
  AssetCreatePage,
  AssetMetricCreatePage,
  AssetMetricPage,
  AssetModelCreatePage,
  AssetModelPage,
  AssetPage,
  AssetProfilesPage,
  MetricCreatePage,
  MetricEditPage,
  ModelCreatePage,
  ProfileCreatePage,
  ProfileModelPage,
  DataSheetsPage,
  DataQualityIssuesPage,
  UploadTemplatePage,
  DownloadTemplatePage,
  ProfileWidgetCreatePage,
  ResourcePage,
  ResourceCreatePage,
  ShiftTablesPage,
  ShiftTablesCreatePage,
} from '@digital/thing-ui';
import { DataReloadPage, NewReloadDataQualityPage, ReloadSummaryPage, NewReloadTsSelectionPage } from '@digital/ts-ui';
import {
  DataTypePage,
  DataTypeCreatePage,
  LibrariesPage,
  LookupTablePage,
  LookupTableCreatePage,
  TimeseriesPage,
  TimeserieCreatePage,
} from '@digital/ts-ui';
import {
  BindTimeseriesPage,
  ConnectionCreatePage,
  ConnectionPage,
  FlowCreatePage,
  FlowPage,
  FlowsPage,
  GatewayConnectionsPage,
  GatewayCreatePage,
  GatewayHomePage,
  GatewayInfoPage,
  GatewayPage,
  ManageTimeseriesPage,
  LogsPage,
  SecurityPage,
  UploadTimeseriesTemplatePage,
} from '@gluon/ui';
import { AuditTrailPage } from '@sms/plasma-audit-ui';
import {
  UserProfilePage,
  ApplicationParameterPage,
  IdentityProviderPage,
  IdentityProviderCreatePage,
  IdentityProviderEditPage,
  RolePage,
  RoleCreatePage,
  UserPage,
  InviteUserPage,
  LoginPage,
  RegisterPage,
  WelcomePage,
  ResetPasswordPage,
  ForgotPasswordPage,
} from '@sms/plasma-security-ui';
import { PageConfig } from '@sms/plasma-ui';
import { DimensionPage, DimensionCreatePage } from '@sms/plasma-uom-ui';

import backgroundImage from '../assets/img/background.jpg';
import app from '../config/app.json';

const LaunchpadPage = lazy(() => import('./LaunchpadPage'));
const HomePage = lazy(() => import('./HomePage'));

const pages: PageConfig[] = [
  {
    key: 'Launchpad',
    path: '/',
    page: LaunchpadPage,
  },
  {
    key: 'UserProfile',
    path: '/userProfile',
    page: UserProfilePage,
  },
  {
    key: 'Login',
    path: '/login',
    page: LoginPage,
    unprotected: true,
    params: {
      image: backgroundImage,
      title: 'app.page.login.title',
      subtitle: 'app.page.login.subtitle',
      forgotPasswordRoutePath: '/forgotPassword',
      signUpRoutePath: '/register',
    },
  },
  {
    key: 'Register',
    path: '/register',
    page: RegisterPage,
    unprotected: true,
    params: {
      app: app.title,
      image: backgroundImage,
      title: 'app.page.register.title',
      subtitle: 'app.page.register.subtitle',
      signInRoutePath: '/login',
      privacyPolicyTitle: 'app.page.register.privacyPolicy.title',
      privacyPolicyRoutePath: process.env.REACT_APP_PORTAL_PRIVACY_POLICY_URL,
    },
  },
  {
    key: 'Welcome',
    path: '/verify',
    page: WelcomePage,
    unprotected: true,
    params: {
      app: app.title,
      image: backgroundImage,
      title: 'app.page.welcome.title',
      subtitle: 'app.page.welcome.subtitle',
      signInRoutePath: '/login',
    },
  },
  {
    key: 'ForgotPassword',
    path: '/forgotPassword',
    page: ForgotPasswordPage,
    unprotected: true,
    params: {
      image: backgroundImage,
      title: 'app.page.forgotPassword.title',
      subtitle: 'app.page.forgotPassword.subtitle',
      signInRoutePath: '/login',
    },
  },
  {
    key: 'ResetPasswordPage',
    path: '/resetPassword',
    page: ResetPasswordPage,
    unprotected: true,
    params: {
      image: backgroundImage,
      title: 'app.page.resetPassword.title',
      subtitle: 'app.page.resetPassword.subtitle',
      signInRoutePath: '/login',
      forgotPasswordRoutePath: '/forgotPassword',
    },
  },
  {
    key: 'Home',
    path: '/admin',
    page: HomePage,
  },
  {
    key: 'AuditTrail',
    path: '/admin/auditTrail',
    page: AuditTrailPage,
    help: '/auditTrail/index.html',
    title: 'app.page.auditTrail',
    permissions: [['log:READ']],
  },
  {
    key: 'ApplicationParameter',
    path: '/admin/parameters',
    page: ApplicationParameterPage,
    title: 'app.page.parameters',
    help: '/system/applicationParameter/index.html',
    permissions: [['application:READ', 'application:WRITE']],
  },
  {
    key: 'IdentityProvider',
    path: '/admin/identityProviders',
    page: IdentityProviderPage,
    title: 'app.page.identityProviders',
    help: '/security/identityProviders/index.html',
    permissions: [['identity_provider:READ', 'identity_provider:WRITE']],
  },
  {
    key: 'IdentityProviderCreate',
    path: '/admin/identityProviders/create',
    page: IdentityProviderCreatePage,
    title: 'app.page.identityProviders',
    help: '/security/identityProviders/create/index.html',
    permissions: [['identity_provider:WRITE']],
  },
  {
    key: 'IdentityProviderEdit',
    path: '/admin/identityProviders/:id',
    page: IdentityProviderEditPage,
    title: 'app.page.identityProviders',
    help: '/security/identityProviders/edit/index.html',
    permissions: [['identity_provider:WRITE']],
  },
  {
    key: 'Role',
    path: '/admin/roles',
    page: RolePage,
    title: 'app.page.roles',
    help: '/roles/index.html',
    permissions: [['role:READ', 'role:WRITE']],
  },
  {
    key: 'RoleCreate',
    path: '/admin/roles/create',
    page: RoleCreatePage,
    title: 'app.page.roles',
    help: '/roles/create/index.html',
    permissions: [['role:WRITE']],
  },
  {
    key: 'User',
    path: '/admin/users',
    page: UserPage,
    title: 'app.page.users',
    help: '/users/index.html',
    permissions: [['user:READ', 'user:WRITE']],
  },
  {
    key: 'InviteUser',
    path: '/admin/users/invite',
    page: InviteUserPage,
    title: 'app.page.users',
    help: '/users/invite/index.html',
    permissions: [['user:INSTANTIATE', 'user:WRITE']],
  },
  {
    key: 'UnitsOfMeasurement',
    path: '/admin/configuration/unitsOfMeasurement',
    page: DimensionPage,
    title: 'app.page.configuration.unitsOfMeasurement',
    help: '/uom/index.html',
    permissions: [['dimension:READ', 'dimension:WRITE']],
  },
  {
    key: 'UnitsOfMeasurementCreate',
    path: '/admin/configuration/unitsOfMeasurement/create',
    page: DimensionCreatePage,
    title: 'app.page.configuration.unitsOfMeasurement',
    help: '/uom/create/index.html',
    permissions: [['dimension:WRITE']],
  },
  {
    key: 'DataTypes',
    path: '/admin/configuration/dataTypes',
    page: DataTypePage,
    title: 'app.page.configuration.dataTypes',
    help: '/dataType/index.html',
    permissions: [['data_type:READ', 'data_type:WRITE']],
  },
  {
    key: 'DataTypesCreate',
    path: '/admin/configuration/dataTypes/create',
    page: DataTypeCreatePage,
    title: 'app.page.configuration.dataTypes',
    help: '/dataType/create/index.html',
    permissions: [['data_type:WRITE']],
  },
  // Using invalid permission to allow only users with administrator privileges
  {
    key: 'LabLibraries',
    path: '/admin/configuration/labLibraries',
    page: LibrariesPage,
    title: 'app.page.viridisLab.libraries',
    params: { title: 'app.page.viridisLab.libraries' },
    permissions: [['notValidPermission']],
  },
  {
    key: 'LookupTables',
    path: '/admin/configuration/lookupTables',
    page: LookupTablePage,
    title: 'app.page.configuration.lookupTables',
    permissions: [['lookup_table:READ', 'lookup_table:WRITE']],
    help: '/lookupTables/index.html',
  },
  {
    key: 'LookupTablesCreate',
    path: '/admin/configuration/lookupTables/create',
    page: LookupTableCreatePage,
    title: 'app.page.configuration.lookupTables',
    permissions: [['lookup_table:WRITE']],
    help: '/lookupTables/create/index.html',
  },
  {
    key: 'Resources',
    path: '/admin/configuration/resources',
    page: ResourcePage,
    title: 'app.page.configuration.resources',
    permissions: [['resource:READ', 'resource:WRITE']],
    help: '/configuration/resources/index.html',
  },
  {
    key: 'ResourcesCreate',
    path: '/admin/configuration/resources/create',
    page: ResourceCreatePage,
    title: 'app.page.configuration.resources',
    permissions: [['resource:WRITE']],
    help: '/configuration/resources/create/index.html',
  },
  {
    key: 'GatewayPage',
    path: '/admin/configuration/gluon',
    page: GatewayPage,
    title: 'app.page.dataCollection.gateways',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'GatewayCreatePage',
    path: '/admin/configuration/gluon/create',
    page: GatewayCreatePage,
    title: 'app.page.dataCollection.gateways.create',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'GatewayHomePage',
    path: '/admin/configuration/gluon/:id',
    page: GatewayHomePage,
    title: 'app.page.dataCollection.gateway',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'GatewayInfoPage',
    path: '/admin/configuration/gluon/:id/info',
    page: GatewayInfoPage,
    title: 'app.page.dataCollection.gateways.details',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'GatewayConnectionsPage',
    path: '/admin/configuration/gluon/:id/connections',
    page: GatewayConnectionsPage,
    title: 'app.page.dataCollection.gateways.connections',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'ConnectionPage',
    path: '/admin/configuration/gluon/:id/connections/:connectionId/edit',
    page: ConnectionPage,
    title: 'app.page.dataCollection.gateways.connections.edit',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'ConnectionCreatePage',
    path: '/admin/configuration/gluon/:id/connections/:connectorId/create',
    page: ConnectionCreatePage,
    title: 'app.page.dataCollection.gateways.connections.create',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'FlowsPage',
    path: '/admin/configuration/gluon/:id/flows',
    page: FlowsPage,
    title: 'app.page.dataCollection.gateways.flows',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'FlowPage',
    path: '/admin/configuration/gluon/:id/flows/:flowId/edit',
    page: FlowPage,
    title: 'app.page.dataCollection.gateways.flows.edit',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'FlowCreatePage',
    path: '/admin/configuration/gluon/:id/flows/create',
    page: FlowCreatePage,
    title: 'app.page.dataCollection.gateways.flows.create',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'ManageTimeseriesPage',
    path: '/admin/configuration/gluon/:id/timeseries/manage/:tsType',
    page: ManageTimeseriesPage,
    title: 'app.page.dataCollection.gateways.timeseries',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'ManageTimeseriesEditPage',
    path: '/admin/configuration/gluon/:id/timeseries/manage/:tsType/:tsId/edit',
    page: ManageTimeseriesPage,
    title: 'app.page.dataCollection.gateways.timeseries',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'BindTimeseriesPage',
    path: '/admin/configuration/gluon/:id/timeseries/manage/:tsType/bind',
    page: BindTimeseriesPage,
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'UploadTimeseriesTemplatePage',
    path: '/admin/configuration/gluon/:id/timeseries/manage/:tsType/upload',
    page: UploadTimeseriesTemplatePage,
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'Logs',
    path: '/admin/configuration/gluon/:id/logs',
    page: LogsPage,
    title: 'app.page.dataCollection.gateways.logs',
    permissions: [['gateway:READ', 'gateway:WRITE']],
  },
  {
    key: 'Security',
    path: '/admin/configuration/gluon/:id/security',
    page: SecurityPage,
    title: 'app.page.dataCollection.gateways.security',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'DataQualityIssuesPage',
    path: '/admin/data-collection/data-quality',
    page: DataQualityIssuesPage,
    title: 'app.page.dataCollection.dataQualityIssues',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'Timeseries',
    path: '/admin/timeseries/timeseries',
    page: TimeseriesPage,
    title: 'app.menu.timeseries',
    permissions: ['cts:READ', 'cts:WRITE', 'sts:READ', 'sts:WRITE'],
  },
  {
    key: 'TimeseriesCreatePage',
    path: '/admin/timeseries/timeseries/create',
    page: TimeserieCreatePage,
    params: { entity: 'ts.commons.entity.timeserie' },
    permissions: ['cts:READ', 'cts:WRITE', 'sts:READ', 'sts:WRITE'],
  },
  {
    key: 'DataReload',
    path: '/admin/timeseries/datareload',
    page: DataReloadPage,
    title: 'app.page.timeseries.datareload',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'ReloadSummary',
    path: '/admin/timeseries/datareload/:id/summary',
    page: ReloadSummaryPage,
    title: 'app.page.timeseries.datareload.summary',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'NewReloadDataQualityPage',
    path: '/admin/timeseries/datareload/new_by_data_quality',
    page: NewReloadDataQualityPage,
    title: 'app.page.timeseries.datareload.new',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'NewReloadTsSelectionPage',
    path: '/admin/timeseries/datareload/new_by_ts_selection',
    page: NewReloadTsSelectionPage,
    title: 'app.page.timeseries.datareload.new',
    permissions: [['gateway: READ', 'gateway:WRITE']],
  },
  {
    key: 'ShiftTables',
    path: '/admin/assets/shiftTables',
    page: ShiftTablesPage,
    title: 'app.page.assets.shiftTables',
    permissions: [['asset:READ', 'asset:WRITE']],
  },
  {
    key: 'NewShiftTable',
    path: '/admin/assets/shiftTables/create',
    page: ShiftTablesCreatePage,
    title: 'app.page.assets.shiftTables.new',
    params: { entity: 'thing.commons.entity.shiftTable' },
    permissions: [['asset:WRITE']],
  },
  {
    key: 'Profiles',
    path: '/admin/assets/profiles',
    page: AssetProfilesPage,
    title: 'app.page.assets.profiles',
    params: { thing_type: 'asset', thisThing: 'thing.commons.this_profile', title: 'profile' },
    help: '/asset/profiles/index.html',
    permissions: [['profile:READ', 'profile:WRITE']],
  },
  {
    key: 'ProfileCreatePage',
    path: '/admin/assets/profiles/create',
    page: ProfileCreatePage,
    title: 'app.page.assets.profiles',
    params: { thing_type: 'asset', entity: 'thing.profiles.entity.profile' },
    help: '/asset/profiles/create/index.html',
    permissions: [['profile:WRITE']],
  },
  {
    key: 'MetricCreatePage',
    path: '/admin/assets/profiles/:profile_id/metric/create',
    page: MetricCreatePage,
    title: 'app.page.profile.metric.create',
    help: '/asset/profiles/metrics/create/index.html',
    permissions: [['profile:WRITE']],
  },
  {
    key: 'MetricEditPage',
    path: '/admin/assets/profiles/:profile/metric/:id',
    page: MetricEditPage,
    title: 'app.page.profile.metric.edit',
    help: '/asset/profiles/metrics/edit/index.html',
    permissions: [['profile:WRITE', 'profile:READ']],
  },
  {
    key: 'ProfileModelPage',
    path: '/admin/assets/profiles/:profileId/model',
    page: ProfileModelPage,
    title: 'app.page.profile.models',
    help: '/asset/profiles/models/edit/index.html',
    permissions: [['profile:READ', 'profile:WRITE']],
  },
  {
    key: 'ModelCreatePage',
    path: '/admin/assets/profiles/:profileId/model/create',
    page: ModelCreatePage,
    title: 'app.page.profile.model.create',
    help: '/asset/profiles/models/create/index.html',
    permissions: [['profile:WRITE']],
  },
  {
    key: 'AssetProfilesMetricModelCreatePage',
    path: '/admin/assets/profiles/:profileId/model/create/:id',
    page: ModelCreatePage,
    title: 'app.page.profile.model.create',
    help: '/asset/profiles/models/create/index.html',
    permissions: [['profile:WRITE']],
  },
  {
    key: 'AssetProfilesWidgetCreatePage',
    path: '/admin/assets/profiles/:profileId/widget/create',
    page: ProfileWidgetCreatePage,
    title: 'app.page.profile.widget.create',
    help: '/asset/profiles/widgets/create/index.html',
    permissions: [['widget:WRITE']],
  },
  {
    key: 'Assets',
    path: '/admin/assets/assets',
    page: AssetPage,
    title: 'app.page.assets.assets',
    params: {
      thing_type: 'asset',
      thisThing: 'thing.commons.this_asset',
      title: 'asset',
      titleTab: 'thing.assets.profiles',
    },
    permissions: [['asset:READ', 'asset:WRITE']],
    help: '/asset/index.html',
  },
  {
    key: 'DataSheets',
    path: '/admin/assets/dataSheets',
    page: DataSheetsPage,
    title: 'app.page.assets.dataSheets',
    params: {
      thing_type: 'asset',
    },
    permissions: [['asset:READ', 'asset:WRITE']],
  },
  {
    key: 'DownloadTemplateWizard',
    path: '/admin/assets/dataSheets/downloadTemplate',
    page: DownloadTemplatePage,
    title: 'app.page.assets.downloadTemplate',
    params: {
      thing_type: 'asset',
    },
    permissions: [['asset:READ', 'asset:WRITE']],
  },
  {
    key: 'UploadTemplateWizard',
    path: '/admin/assets/dataSheets/uploadTemplate',
    page: UploadTemplatePage,
    title: 'app.page.assets.uploadTemplate',
    params: {
      thing_type: 'asset',
    },
    permissions: [['asset:READ', 'asset:WRITE']],
  },
  {
    key: 'AssetsCreate',
    path: '/admin/assets/assets/create',
    page: AssetCreatePage,
    title: 'app.page.assets.assets',
    params: { thing_type: 'asset', entity: 'thing.assets.entity.asset' },
    permissions: [['asset:WRITE']],
    help: '/asset/create/index.html',
  },
  {
    key: 'AssetsCreate',
    path: '/admin/assets/assets/:thingId/create',
    page: AssetCreatePage,
    title: 'app.page.assets.assets',
    params: { thing_type: 'asset', entity: 'thing.assets.entity.asset' },
    permissions: [['asset:WRITE']],
    help: '/asset/create/index.html',
  },
  {
    key: 'AssetMetricCreatePage',
    path: '/admin/assets/assets/:thingId/metric/create',
    page: AssetMetricCreatePage,
    title: 'app.page.assets.metric.create',
    params: { thing_type: 'asset', title: 'asset' },
    help: '/asset/metric/create/index.html',
    permissions: [['asset:WRITE']],
  },
  {
    key: 'AssetMetricEditPage',
    path: '/admin/assets/assets/:thingId/metric/:id',
    page: AssetMetricPage,
    title: 'app.page.assets.metric.edit',
    params: { thing_type: 'asset', title: 'asset' },
    permissions: [['asset:READ', 'asset:WRITE']],
    help: '/asset/metric/edit/index.html',
  },
  {
    key: 'AssetConfigureTimeSeriesPage',
    path: '/admin/assets/assets/:thingId/assetConfigureTimeSeries',
    page: AssetConfigureTimeSeriesPage,
    title: 'thing.assets.configureTimeSeries',
    params: { thing_type: 'asset' },
    help: '/asset/metric/configureTimeSeries/index.html',
    permissions: [['asset:READ', 'asset:WRITE']],
  },
  {
    key: 'AssetModelCreatePage',
    path: '/admin/assets/assets/:thingId/model/create',
    page: AssetModelCreatePage,
    title: 'app.page.profile.model.create',
    permissions: [['asset:WRITE']],
    help: '/asset/model/create/index.html',
  },
  {
    key: 'AssetMetricModelCreatePage',
    path: '/admin/assets/assets/:thingId/model/create/:id',
    page: AssetModelCreatePage,
    title: 'app.page.profile.model.create',
    permissions: [['asset:WRITE']],
    help: '/asset/model/create/index.html',
  },
  {
    key: 'AssetModelPage',
    path: '/admin/assets/assets/:thingId/model/:id',
    page: AssetModelPage,
    title: 'app.page.profile.models',
    permissions: [['asset:READ', 'asset:WRITE']],
    params: { thing_type: 'asset' },
    help: '/asset/model/edit/index.html',
  },
];

export default pages;
